import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type DateRangeValue = {
    From: string | null;
    To: string | null;
};

// Date-time range filter control: two Kendo DateTimePickers behind one form control whose value is
// { From, To } as ISO strings, or null when both ends are empty. That is the contract the list filter
// models and BaseKendoList.clearAllDateRanges() rely on. Replaces angular-datetimerangepicker, which
// was View Engine only and unmaintained since 2022.
@Component({
    selector: 'form-datetimerange',
    styleUrls: ['form-controls.component.scss'],
    template: `
        <div class="form-group" [ngClass]="{ 'has-value': !!value }">
            <label class="col-form-label">{{ label }}</label>
            <div class="datetimerange-inputs">
                <kendo-datetimepicker
                    class="k-datetimepicker noborder form-control form-control-sm pcs-control"
                    format="dd.MM.yyyy HH:mm"
                    placeholder="{{ 'From' | translate }}"
                    [disabled]="isDisabled"
                    [value]="from"
                    (valueChange)="fromChanged($event)">
                    <kendo-datetimepicker-messages
                        today="{{ 'Today' | translate }}"
                        dateTab="{{ 'Date' | translate }}"
                        timeTab="{{ 'Time' | translate }}"
                        now="{{ 'Now' | translate }}"
                        accept="{{ 'Accept' | translate }}"
                        cancel="{{ 'Cancel' | translate }}"></kendo-datetimepicker-messages>
                </kendo-datetimepicker>
                <kendo-datetimepicker
                    class="k-datetimepicker noborder form-control form-control-sm pcs-control"
                    format="dd.MM.yyyy HH:mm"
                    placeholder="{{ 'To' | translate }}"
                    [disabled]="isDisabled"
                    [value]="to"
                    (valueChange)="toChanged($event)">
                    <kendo-datetimepicker-messages
                        today="{{ 'Today' | translate }}"
                        dateTab="{{ 'Date' | translate }}"
                        timeTab="{{ 'Time' | translate }}"
                        now="{{ 'Now' | translate }}"
                        accept="{{ 'Accept' | translate }}"
                        cancel="{{ 'Cancel' | translate }}"></kendo-datetimepicker-messages>
                </kendo-datetimepicker>
                @if (value && !isDisabled) {
                    <i class="k-icon k-clear-value k-i-x noborder date-time-close" (click)="clearRange()"></i>
                }
            </div>
        </div>
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: DatetimerangeComponent,
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DatetimerangeComponent implements ControlValueAccessor {
    @Input()
    label: string;

    from: Date | null = null;
    to: Date | null = null;
    isDisabled = false;

    private onChange: (value: DateRangeValue | null) => void = () => {};
    private onTouched: () => void = () => {};

    get value(): DateRangeValue | null {
        return this.buildValue();
    }

    fromChanged(date: Date | null): void {
        this.from = date;
        this.emit();
    }

    toChanged(date: Date | null): void {
        this.to = date;
        this.emit();
    }

    clearRange(): void {
        this.from = null;
        this.to = null;
        this.emit();
    }

    writeValue(obj: DateRangeValue | null): void {
        this.from = this.parse(obj?.From);
        this.to = this.parse(obj?.To);
    }

    registerOnChange(fn: (value: DateRangeValue | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    private emit(): void {
        this.onChange(this.buildValue());
        this.onTouched();
    }

    private buildValue(): DateRangeValue | null {
        const value = { From: this.toIso(this.from), To: this.toIso(this.to) };
        return value.From || value.To ? value : null;
    }

    private toIso(date: Date | null): string | null {
        return date instanceof Date && !isNaN(date.getTime()) ? date.toISOString() : null;
    }

    private parse(value: string | null | undefined): Date | null {
        if (!value) {
            return null;
        }
        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date;
    }
}
